import {
  toLadCameraAzimuthLineFeatures,
  toLadCameraSectorFeatures,
} from '@components/map/layers/ladCameras/ladCamera.mapper';
import { toNeutralSectorCoverageFeatures } from '@components/map/layers/neutralSectors/neutral.mapper';
import { toRadarCoverageFeature } from '@components/map/layers/radarCoverage/radarCoverage.mapper';
import { selectSelectedCameraStreams } from '@redux/global/global.selector';
import { selectActualMapFilters } from '@redux/settings/settings.selectors';
import {
  selectFilteredActiveMonitoringSensorsWithValidPosition,
  selectNeutralActiveMonitoringeSensorWithSectors,
} from '@redux/situation/monitoring.selectors';
import { RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { isCameraActiveMonitoringSensor } from '@utils/sensors/camera/camera.utils';
import { getRadarConfiguration, isRadarSensor } from '@utils/sensors/sensors.utils';
import { Feature, FeatureCollection } from 'geojson';

import { MapFilters } from '@/types/filters.types';
import { RadarConfiguration } from '@/types/sensor/configuration.types';
import {
  ActiveMonitoringSensor,
  CameraActiveMonitoringSensor,
  NeutralActiveMonitoringWithSectors,
} from '@/types/sensor/monitoring.types';
import { SubCameraConfiguration } from '@/types/sensor/sensors';

//TODO : Complete with all map displaying selectors

export const selectCommonRadarConfigurations = createSelector(
  selectFilteredActiveMonitoringSensorsWithValidPosition,
  (monitoringSensors: ActiveMonitoringSensor[]): RadarConfiguration[] =>
    monitoringSensors
      .map((monitoringSensor) => monitoringSensor.configuration)
      .filter(isRadarSensor)
      .filter((sensor) => !sensor.maintenance)
      .flatMap<RadarConfiguration>(getRadarConfiguration)
      .filter((r) => r.active && !r.maintenance),
);

export const selectRadarCoverageFeatures = createSelector(
  selectCommonRadarConfigurations,
  selectActualMapFilters,
  (radars: RadarConfiguration[], mapFilters: MapFilters): FeatureCollection => ({
    type: 'FeatureCollection',
    features: mapFilters.displaySensorInfoCoverage ? radars.flatMap((radar) => toRadarCoverageFeature(radar)) : [],
  }),
);

export const selectNeutralSectorsFeatureCollection = createSelector(
  selectNeutralActiveMonitoringeSensorWithSectors,
  (monitoringSensors: NeutralActiveMonitoringWithSectors[]): FeatureCollection => {
    const displayableSensors = monitoringSensors.filter(
      (monitoringSensor) => !monitoringSensor.configuration.maintenance,
    );
    return {
      type: 'FeatureCollection',
      features: toNeutralSectorCoverageFeatures(displayableSensors),
    };
  },
);

export const selectFilteredCameraMonitoringSensors = createSelector(
  selectFilteredActiveMonitoringSensorsWithValidPosition,
  (monitoringSensors: ActiveMonitoringSensor[]): CameraActiveMonitoringSensor[] =>
    monitoringSensors
      .filter(isCameraActiveMonitoringSensor)
      .filter((monitoringSensor) => !monitoringSensor.configuration.maintenance),
);

export const selectLadCameraSectorFeatures = createSelector(
  [
    selectFilteredCameraMonitoringSensors,
    selectActualMapFilters,
    (state: RootState, replayMode?: boolean) => (replayMode ? [] : selectSelectedCameraStreams(state)),
  ],
  (
    monitoringSensors: CameraActiveMonitoringSensor[],
    mapFilters: MapFilters,
    selectedCameraStreams: SubCameraConfiguration[],
  ): Feature[] => {
    if (!mapFilters.displaySensorInfoLadCameraFov) {
      return [];
    }
    const displayableSelectedCameraStreams = selectedCameraStreams.filter(
      (stream) => stream.active && !stream.maintenance,
    );
    return toLadCameraSectorFeatures(monitoringSensors, displayableSelectedCameraStreams);
  },
);

export const selectLadCameraAzimuthLineFeatures = createSelector(
  selectFilteredCameraMonitoringSensors,
  selectActualMapFilters,
  (state: RootState, replayMode?: boolean) => (replayMode ? [] : selectSelectedCameraStreams(state)),
  (
    monitoringSensors: CameraActiveMonitoringSensor[],
    mapFilters: MapFilters,
    selectedCameraStreams: SubCameraConfiguration[],
  ): Feature[] => {
    if (!mapFilters.displaySensorInfoLadCameraFov) {
      return [];
    }
    const displayableSelectedCameraStreams = selectedCameraStreams.filter(
      (stream) => stream.active && !stream.maintenance,
    );
    return toLadCameraAzimuthLineFeatures(monitoringSensors, displayableSelectedCameraStreams);
  },
);

export const selectLadCameraCoverageFeatures = createSelector(
  [selectLadCameraSectorFeatures, selectLadCameraAzimuthLineFeatures],
  (sectors, azimuthLines): FeatureCollection => {
    return {
      type: 'FeatureCollection',
      features: [...sectors, ...azimuthLines],
    };
  },
);
